/**
 * Optimizer Panel — glues together species picker + score curve chart.
 *
 * Lives below the 3D scene as a separate strip so the deputy can scrub the
 * curve, change game animal, and watch the build's sweet-spot band shift.
 */

import { useState, useMemo } from 'react';
import type { Build, Environment } from '../../lib/trajectory/types';
import { computeForgeScoreCurve } from '../../lib/forgeScore/curve';
import { SPECIES_PROFILES, getSpecies } from '../../lib/forgeScore/speciesProfiles';
import { ForgeScoreCurveChart } from './ForgeScoreCurveChart';

interface Props {
  buildA: Build;
  buildB: Build | null;
  env: Environment;
  onArrowAGrainsChange: (g: number) => void;
  onArrowBGrainsChange?: (g: number) => void;
}

export function OptimizerPanel({
  buildA,
  buildB,
  env,
  onArrowAGrainsChange,
  onArrowBGrainsChange,
}: Props) {
  const [speciesTag, setSpeciesTag] = useState(SPECIES_PROFILES[3].tag); // default Elk
  const species = getSpecies(speciesTag) ?? SPECIES_PROFILES[3];

  const curveA = useMemo(
    () => computeForgeScoreCurve(buildA, env, species),
    [buildA, env, species]
  );
  const curveB = useMemo(
    () => (buildB ? computeForgeScoreCurve(buildB, env, species) : null),
    [buildB, env, species]
  );

  return (
    <section className="optimizer-panel">
      <header className="optimizer-header">
        <h3>Build Optimizer</h3>
        <div className="species-picker">
          <label>Game</label>
          <select
            value={speciesTag}
            onChange={(e) => setSpeciesTag(e.target.value as typeof speciesTag)}
          >
            {SPECIES_PROFILES.map((s) => (
              <option key={s.tag} value={s.tag}>{s.label}</option>
            ))}
          </select>
        </div>
      </header>

      {species.anchor && (
        <div className="optimizer-anchor">
          <strong>Anchor:</strong> {species.anchor}
        </div>
      )}

      <div className="optimizer-curves">
        <ForgeScoreCurveChart
          curve={curveA}
          color="ember"
          species={species.label}
          onArrowWeightChange={onArrowAGrainsChange}
        />
        {curveB && onArrowBGrainsChange && (
          <ForgeScoreCurveChart
            curve={curveB}
            color="cyan"
            species={species.label}
            onArrowWeightChange={onArrowBGrainsChange}
          />
        )}
      </div>
    </section>
  );
}
